import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { formatClockTime } from "@/lib/formatTime";

interface GeolocationData {
  latitude: number | null;
  longitude: number | null;
  timestamp: number | null;
  accuracy: number | null;
}

export interface PunchRecord {
  type: "IN" | "OUT";
  time: string;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
}

export interface TodayPunchStatus {
  hasPunchedIn: boolean;
  hasPunchedOut: boolean;
  punchInTime: string | null;
  punchOutTime: string | null;
  minInTime: string | null;
  maxOutTime: string | null;
  totalDurationSeconds: number | null;
  lastEventType: "IN" | "OUT" | null;
  records: any[];
}

interface StatusMessage {
  type: "info" | "error" | "success";
  text: string;
}

interface AttendanceState {
  geolocation: GeolocationData;
  punchRecords: PunchRecord[];
  todayPunchStatus: TodayPunchStatus;
  message: StatusMessage | null;
  permissionDenied: boolean;
  cameraPermissionDenied: boolean;
  appLoading: boolean;
  statusLoading: boolean;
}

const defaultPunchStatus: TodayPunchStatus = {
  hasPunchedIn: false,
  hasPunchedOut: false,
  punchInTime: null,
  punchOutTime: null,
  minInTime: null,
  maxOutTime: null,
  totalDurationSeconds: null,
  lastEventType: null,
  records: [],
};

const initialState: AttendanceState = {
  geolocation: { latitude: null, longitude: null, timestamp: null, accuracy: null },
  punchRecords: [],
  todayPunchStatus: defaultPunchStatus,
  message: null,
  permissionDenied: false,
  cameraPermissionDenied: false,
  appLoading: false,
  statusLoading: false,
};

export const fetchTodayPunchStatus = createAsyncThunk(
  "attendance/fetchTodayPunchStatus",
  async (userId: number, { rejectWithValue }) => {
    const response = await fetch(`/api/attendance/today?userId=${userId}`);
    if (!response.ok) return rejectWithValue("Failed to fetch punch status");
    return response.json();
  }
);

export const recordPunch = createAsyncThunk(
  "attendance/recordPunch",
  async (
    payload: {
      userId: number;
      eventType: "IN" | "OUT";
      latitude: number;
      longitude: number;
      selfie: File;
    },
    { rejectWithValue }
  ) => {
    const formData = new FormData();
    formData.append("userId", String(payload.userId));
    formData.append("eventType", payload.eventType);
    formData.append("latitude", String(payload.latitude));
    formData.append("longitude", String(payload.longitude));
    formData.append("selfie", payload.selfie, "selfie.jpg");

    // No Content-Type header — the browser sets the multipart boundary.
    const response = await fetch("/api/attendance/punch", {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) return rejectWithValue(data.error || "Failed to record punch");
    return data as { message: string };
  }
);

function toDisplayRecords(records: any[]): PunchRecord[] {
  return records.map((record) => ({
    type: record.event_type,
    time: formatClockTime(new Date(record.event_time)),
    latitude: record.latitude,
    longitude: record.longitude,
    accuracy: null,
  }));
}

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    setGeolocation(state, action: PayloadAction<GeolocationData>) {
      state.geolocation = action.payload;
    },
    setMessage(state, action: PayloadAction<StatusMessage | null>) {
      state.message = action.payload;
    },
    setPermissionDenied(state, action: PayloadAction<boolean>) {
      state.permissionDenied = action.payload;
    },
    setCameraPermissionDenied(state, action: PayloadAction<boolean>) {
      state.cameraPermissionDenied = action.payload;
    },
    setAppLoading(state, action: PayloadAction<boolean>) {
      state.appLoading = action.payload;
    },
    resetAttendance() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTodayPunchStatus.pending, (state) => {
        state.statusLoading = true;
      })
      .addCase(fetchTodayPunchStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.todayPunchStatus = action.payload;
        if (Array.isArray(action.payload.records)) {
          state.punchRecords = toDisplayRecords(action.payload.records);
        }
      })
      .addCase(fetchTodayPunchStatus.rejected, (state) => {
        state.statusLoading = false;
      })
      .addCase(recordPunch.pending, (state) => {
        state.appLoading = true;
      })
      .addCase(recordPunch.fulfilled, (state, action) => {
        state.appLoading = false;
        state.message = { type: "success", text: action.payload.message };
      })
      .addCase(recordPunch.rejected, (state, action) => {
        state.appLoading = false;
        state.message = {
          type: "error",
          text: (action.payload as string) || "Failed to record punch",
        };
      });
  },
});

export const {
  setGeolocation,
  setMessage,
  setPermissionDenied,
  setCameraPermissionDenied,
  setAppLoading,
  resetAttendance,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;
